/** @jsx jsx */
import { css, jsx } from '@emotion/react';
import React, { useContext, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import * as d3 from 'd3';
import { KeyframeCreator } from './KeyframeCreator';
import { SRSTimeScale, TimelineContext } from 'utilities/srs-timeline-library/timeline';

const MIN_WIDTH = 16;
export const GRABBER = {
	WIDTH: 3,
	HIT_WIDTH: 6,
};
const styles = {
	keyframe: css`
		.keyframe {
			fill: transparent;
			cursor: grab;

			&:active {
				cursor: grabbing;
			}

			transition: opacity 0.5s linear;
			animation: fade-in 250ms;
		}

		.grabber {
			cursor: ew-resize;
			fill: transparent;

			.visible {
				fill: black;
			}
		}
	`,
	graphOverlay: css`
		fill: rgba(255, 255, 255, 0.7);
	`,
};

export function Keyframe({ keyframe, updateKeyframe }) {

	const chartContext = useContext(TimelineContext);
	const { xScale, dataHeight, dataWidth, margin } = chartContext;

	const [x, setX] = useState<number | null>();
	const [keyframeWidth, setKeyframeWidth] = useState<number | null>();
	const [isKeyframeStale, setIsKeyframeStale] = useState(false);
	const keyframeRef = useRef<SVGGElement>(null);

	// When the keyframe changes update localState
	useLayoutEffect(() => {
		if (keyframe && xScale) {
			const { x, keyframeWidth } = calculateKeyframeAttributes(keyframe, xScale);
			setX(x);
			setKeyframeWidth(keyframeWidth);
		}
	}, [keyframe, xScale]);

	// Update when keyframe data becomes stale.
	useEffect(() => {
		if (isKeyframeStale) {
			const keyframeExtents = [xScale.invert(x), xScale.invert(x + keyframeWidth)];
			setIsKeyframeStale(false);
			updateKeyframe(keyframeExtents);
		}
	}, [xScale, updateKeyframe, isKeyframeStale, x, keyframeWidth]);

	// Don't render anything if a keyframe doesn't exist or if the dimensions haven't been calculated yet.
	const isKeyframeValid = !!keyframe && x && keyframeWidth;

	function onDragEnd() {
		setIsKeyframeStale(true);
	}

	if (isKeyframeValid) {
		// const [minX, maxX] = originalScale.range();
		// Validate & set the x, y values of the keyframe.
		// @ts-ignore
		function updateKeyframeState(x, width) {
			const [minX, maxX] = xScale.domain().map(xScale);
			const isKeyframeInView = validateKeyframe(minX, maxX, width, x);
			if (isKeyframeInView) {
				setX(x);
				setKeyframeWidth(width);
			}
		}

		createKeyframeInteractions(keyframeRef, updateKeyframeState, onDragEnd, x, keyframeWidth);
	}

	return (
		<React.Fragment>

			<AnimatePresence>
				{isKeyframeValid ? (
					<motion.g
						key={'graphOverlay'}
						ref={keyframeRef}
						initial={{ opacity: 0 }}
						animate={{ opacity: 1 }}
						exit={{ opacity: 0 }}
					>
						<g css={styles.graphOverlay}>
							<rect
								x={margin.left}
								width={x - margin.left - GRABBER.WIDTH}
								y={margin.top}
								height={dataHeight}
							/>
							<rect
								x={x + keyframeWidth + GRABBER.WIDTH}
								width={dataWidth - (x + keyframeWidth + GRABBER.WIDTH)}
								y={margin.top}
								height={dataHeight}
							/>
						</g>
					</motion.g>
				) : null}
			</AnimatePresence>

			<KeyframeCreator newKeyframe={updateKeyframe} chartContext={chartContext} />

			<AnimatePresence>
				{isKeyframeValid ? (
					<motion.g
						key={'keyframe'}
						css={styles.keyframe}
						ref={keyframeRef}
						initial={{ opacity: 0 }}
						animate={{ opacity: 1 }}
						exit={{ opacity: 0 }}
					>
						<g className={'grabber left'}>
							<rect
								y={margin.top}
								height={dataHeight}
								x={x - GRABBER.WIDTH / 2 - GRABBER.HIT_WIDTH / 2}
								width={GRABBER.HIT_WIDTH}
							/>
							<rect
								className="visible"
								y={margin.top}
								height={dataHeight}
								x={x - GRABBER.WIDTH}
								width={GRABBER.WIDTH}
							/>
						</g>

						<rect
							className="keyframe"
							y={margin.top} x={x}
							width={keyframeWidth}
							height={dataHeight}
						/>

						<g className={'grabber right'}>
							<rect
								y={margin.top}
								height={dataHeight}
								x={x + keyframeWidth + GRABBER.WIDTH / 2 - GRABBER.HIT_WIDTH / 2}
								width={GRABBER.HIT_WIDTH}
							/>
							<rect
								className="visible"
								y={margin.top}
								height={dataHeight}
								x={x + keyframeWidth}
								width={GRABBER.WIDTH}
							/>
						</g>
					</motion.g>
				) : null}
			</AnimatePresence>

		</React.Fragment>
	);
}

// Active Keyframe helpers.
function createKeyframeInteractions(
	keyframeRef: React.MutableRefObject<SVGGElement>,
	updateKeyframe: (x: any, width: any) => void,
	onDragEnd,
	x: number,
	keyframeWidth: number
) {
	const d3Keyframe = d3.select(keyframeRef.current);

	// Drag for right bar.
	const rightBar = d3Keyframe.select('.grabber.right');
	const rightBarResize = d3
		.drag()
		.on('drag', function (event) {
			// @ts-ignore
			let offset = d3.pointer(event /*this.parentNode*/)[0];

			const newWidth = offset - x;
			updateKeyframe(x, newWidth);
		})
		.on('end', onDragEnd);

	rightBar.call(rightBarResize);

	// Drag for left bar
	const leftBar = d3Keyframe.select('.left');
	const leftBarResize = d3
		.drag()
		.on('drag', function (event) {
			// @ts-ignore
			let offset = d3.pointer(event /*this.parentNode*/)[0];
			const newWidth = keyframeWidth - (offset - x) - GRABBER.WIDTH;
			const newX = offset + GRABBER.WIDTH;
			updateKeyframe(newX, newWidth);
		})
		.on('end', onDragEnd);

	leftBar.call(leftBarResize);

	// Drag for keyframe
	const activeKeyframe = d3.select('.keyframe');
	const positionData = { x };
	activeKeyframe.data([positionData]);
	// activeKeyframe.attr('x', d => d.x);
	const keyframeDrag = d3
		.drag<SVGGElement, typeof positionData>()
		.on('drag', function (event, d) {
			// @ts-ignore
			let offset = d.x + event.dx;
			// @ts-ignore
			d.x = offset;
			updateKeyframe(offset, keyframeWidth);
		})
		.on('end', onDragEnd);

	activeKeyframe.call(keyframeDrag);
}

function calculateKeyframeAttributes(keyframe: [Date, Date], xScale: SRSTimeScale) {
	const scaledExtent = keyframe.map(xScale);
	const x = scaledExtent[0];
	const width = scaledExtent[1] - scaledExtent[0];
	return { x, keyframeWidth: width };
}

function validateKeyframe(minX, maxX, width, x) {
	const MAX_WIDTH = maxX - minX;

	// Rules for valid x, width values.
	const isTooBig = width > MAX_WIDTH;
	const isTooSmall = width < MIN_WIDTH;
	const isTooRight = x < minX - 1; // For extra padding
	const isTooLeft = x + width > maxX;

	const isKeyframeInView = !(isTooBig || isTooSmall || isTooRight || isTooLeft);
	return isKeyframeInView;
}
